// Page-based pagination dots for the product add-on carousels (Add a card / Add
// gifts). The stock slideshow renders one dot per slide, so when several tiles
// are visible per view the active dot leaps by a whole page as the customer
// scrolls. This renders ONE dot per page (the group of tiles visible at once)
// instead, so the active dot moves a single step at a time.
//
// Why runtime measurement: the add-on pickers show a different number of tiles
// per view on desktop / tablet / mobile, so "how many pages" only resolves once
// laid out. We read the scroller's real pixel widths (and re-read on resize) so
// the dot count stays correct across breakpoints — no per-breakpoint Liquid math.
//
// Markup contract (rendered by snippets/resource-list-carousel.liquid):
//   <carousel-page-dots hidden>
//     <ol class="slideshow-controls__dots" data-page-dots></ol>
//   </carousel-page-dots>
// The <ol> reuses the stock dot styling. The buttons carry NO `ref` and NO
// `animation-timeline`, so the slideshow-component's own per-slide dot tracking
// ignores them and we drive `aria-current` ourselves.
class CarouselPageDots extends HTMLElement {
  #slideshow = null;
  #scroller = null;
  #list = null;
  #pages = 1;
  #perView = 1;
  #raf = 0;
  #resizeObserver = null;

  connectedCallback() {
    this.#slideshow = this.closest('slideshow-component');
    this.#scroller = this.#slideshow?.querySelector('slideshow-slides');
    this.#list = this.querySelector('[data-page-dots]');
    if (!this.#scroller || !this.#list) return;

    this.#build();

    this.#scroller.addEventListener('scroll', this.#onScroll, { passive: true });
    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#build());
      this.#resizeObserver.observe(this.#scroller);
    }
    this.#slideshow?.addEventListener('addon-carousel:refresh', this.#onRefresh);
  }

  disconnectedCallback() {
    this.#scroller?.removeEventListener('scroll', this.#onScroll);
    this.#resizeObserver?.disconnect();
    this.#slideshow?.removeEventListener('addon-carousel:refresh', this.#onRefresh);
    if (this.#raf) cancelAnimationFrame(this.#raf);
  }

  // Measure tiles-per-view from the gap between the first two slides (which
  // includes the column gap) against the scroller's visible width.
  #measure() {
    const slides = this.#scroller.querySelectorAll('slideshow-slide:not([hidden])');
    const count = slides.length;
    let step = this.#scroller.clientWidth;
    if (slides.length >= 2) {
      const delta = slides[1].offsetLeft - slides[0].offsetLeft;
      if (delta > 0) step = delta;
    }
    const perView = Math.max(1, Math.round(this.#scroller.clientWidth / step));
    const pages = Math.max(1, Math.ceil(count / perView));
    return { count, perView, pages };
  }

  #build() {
    const { perView, pages } = this.#measure();
    this.#perView = perView;
    this.#pages = pages;

    // A single page needs no pagination — hide the control entirely. Still sync
    // tile visibility so the (all-visible) tiles aren't left aria-hidden/inert.
    if (pages <= 1) {
      this.hidden = true;
      this.#list.replaceChildren();
      this.#syncVisibility();
      return;
    }
    this.hidden = false;

    if (this.#list.childElementCount !== pages) {
      const frag = document.createDocumentFragment();
      for (let i = 0; i < pages; i++) {
        const li = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'slideshow-control button button-unstyled';
        button.setAttribute('aria-label', `Go to page ${i + 1} of ${pages}`);
        button.textContent = `${i + 1}`;
        button.addEventListener('click', () => this.#goToPage(i));
        li.appendChild(button);
        frag.appendChild(li);
      }
      this.#list.replaceChildren(frag);
    }

    this.#update();
  }

  #goToPage(page) {
    // Navigate within the VISIBLE slides (the variant filter hides interspersed
    // slides), then map the target back to its real DOM index — slideshow's
    // select() indexes into ALL slides.
    const visible = this.#scroller.querySelectorAll('slideshow-slide:not([hidden])');
    if (!visible.length) return;
    const target = visible[Math.min(page * this.#perView, visible.length - 1)];
    if (!target) return;

    // Prefer the slideshow's own select() so its arrows and disabled state stay
    // in sync; fall back to a direct scroll if the API isn't available.
    if (typeof this.#slideshow?.select === 'function') {
      const all = this.#scroller.querySelectorAll('slideshow-slide');
      const domIndex = Array.prototype.indexOf.call(all, target);
      if (domIndex !== -1) this.#slideshow.select(domIndex);
    } else {
      this.#scroller.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    }
  }

  // Rebuilds the dot count when the variant filter shows/hides slides.
  #onRefresh = () => this.#build();

  #onScroll = () => {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      this.#update();
    });
  };

  // Keep aria-hidden/inert in sync with what's actually on-screen. The shared
  // slideshow markup (snippets/slideshow-slide.liquid) renders every tile after
  // the first as aria-hidden="true". For a multi-tile scroll view that is both
  // wrong (several tiles ARE visible) and an a11y defect: focusable tile inputs
  // sit inside an aria-hidden subtree (aria-hidden-focus). Mark visible tiles
  // accessible and off-screen tiles inert, so off-screen tiles leave the tab
  // order WITHOUT making the visible, selectable tiles inert.
  #syncVisibility() {
    const { scrollLeft, clientWidth } = this.#scroller;
    const slides = this.#scroller.querySelectorAll('slideshow-slide');
    for (const slide of slides) {
      // Variant-filtered-out tiles carry [hidden] (display:none) — already out
      // of the a11y tree and tab order, so leave them untouched.
      if (slide.hasAttribute('hidden')) continue;
      const left = slide.offsetLeft;
      const right = left + slide.offsetWidth;
      const visible = right > scrollLeft + 1 && left < scrollLeft + clientWidth - 1;
      slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
      slide.toggleAttribute('inert', !visible);
    }
  }

  #update() {
    this.#syncVisibility();
    if (this.#pages <= 1) return;
    const { scrollLeft, clientWidth, scrollWidth } = this.#scroller;
    let page = Math.round(scrollLeft / clientWidth);
    // Pin the last dot once scrolled to the end (the final page is often partial).
    if (scrollLeft + clientWidth >= scrollWidth - 2) page = this.#pages - 1;
    page = Math.max(0, Math.min(page, this.#pages - 1));

    const items = this.#list.children;
    for (let i = 0; i < items.length; i++) {
      items[i].firstElementChild?.setAttribute('aria-current', `${i === page}`);
    }
  }
}

if (!customElements.get('carousel-page-dots')) {
  customElements.define('carousel-page-dots', CarouselPageDots);
}
